import React, { useState, useEffect } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import SeasonSequenceBucketRow from "./SeasonSequenceBucketRow";
import { updateBucket, addBucket, deleteBucket, reorderBuckets } from "../services/seasonSequenceApi";

export default function SeasonSequenceBucketList(props) {
  const comCode = props.comCode;
  const trackingYear = props.trackingYear;
  const buckets = props.buckets;
  const onBucketsChanged = props.onBucketsChanged;

  // Local, optimistically-reorderable copy of the server list. Re-seeded whenever
  // the parent refetches (props.buckets changes), so a successful or failed
  // reorder reconciles/reverts to server truth via onBucketsChanged().
  const [displayBuckets, setDisplayBuckets] = useState(buckets);
  useEffect(() => { setDisplayBuckets(buckets); }, [buckets]);

  const [reordering, setReordering] = useState(false);
  const [reorderError, setReorderError] = useState(null);

  const [editingSequence, setEditingSequence] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editMapsTo, setEditMapsTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [renameError, setRenameError] = useState(null);

  const [newBucketName, setNewBucketName] = useState("");
  const [newBucketMapsTo, setNewBucketMapsTo] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  const [togglingSequence, setTogglingSequence] = useState(null);
  const [toggleErrorSequence, setToggleErrorSequence] = useState(null);
  const [toggleError, setToggleError] = useState(null);

  const [deletingSequence, setDeletingSequence] = useState(null);
  const [deleteErrorSequence, setDeleteErrorSequence] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  // A bucket participates in the chronological order iff it has a position
  // (Other is calendar_position === null and is pinned last, no controls).
  const isPositioned = (b) => b.calendar_position != null;

  const handleMove = (bucket, direction) => {
    const seasonal = displayBuckets.filter(isPositioned);
    const others = displayBuckets.filter((b) => !isPositioned(b));
    const i = seasonal.findIndex((b) => b.season_sequence === bucket.season_sequence);
    const j = direction === "up" ? i - 1 : i + 1;
    if (i < 0 || j < 0 || j >= seasonal.length) return;

    const reordered = [...seasonal];
    [reordered[i], reordered[j]] = [reordered[j], reordered[i]];
    const orderedSeqs = reordered.map((b) => b.season_sequence);

    setDisplayBuckets([...reordered, ...others]); // optimistic
    setReordering(true);
    setReorderError(null);

    reorderBuckets({ com_code: comCode, trackingyear: trackingYear, ordered_season_sequences: orderedSeqs })
      .then(() => onBucketsChanged())            // refetch reconciles with server truth
      .catch((err) => { setReorderError(err.message); onBucketsChanged(); }) // refetch reverts
      .finally(() => setReordering(false));
  };

  const handleEditClick = (bucket) => {
    setEditingSequence(bucket.season_sequence);
    setEditValue(bucket.sequence_name);
    setEditMapsTo(bucket.maps_to_trackingseason == null ? "" : String(bucket.maps_to_trackingseason));
    setRenameError(null);
  };

  const handleCancelClick = () => {
    setEditingSequence(null);
    setEditValue("");
    setEditMapsTo("");
    setRenameError(null);
  };

  const handleSaveClick = (bucket) => {
    setSaving(true);
    setRenameError(null);

    updateBucket({
      com_code: comCode,
      trackingyear: trackingYear,
      season_sequence: bucket.season_sequence,
      sequence_name: editValue.trim(),
      maps_to_trackingseason: editMapsTo === "" ? null : Number(editMapsTo),
    })
      .then(() => {
        setEditingSequence(null);
        setEditValue("");
        setEditMapsTo("");
        onBucketsChanged();
      })
      .catch((err) => setRenameError(err.message))
      .finally(() => setSaving(false));
  };

  const handleAddClick = () => {
    setAdding(true);
    setAddError(null);

    addBucket({
      com_code: comCode,
      trackingyear: trackingYear,
      sequence_name: newBucketName.trim(),
      maps_to_trackingseason: newBucketMapsTo === "" ? null : Number(newBucketMapsTo),
    })
      .then(() => {
        setNewBucketName("");
        setNewBucketMapsTo("");
        onBucketsChanged();
      })
      .catch((err) => setAddError(err.message))
      .finally(() => setAdding(false));
  };

  const handleToggleActiveClick = (bucket) => {
    setTogglingSequence(bucket.season_sequence);
    setToggleError(null);
    setToggleErrorSequence(null);

    updateBucket({
      com_code: comCode,
      trackingyear: trackingYear,
      season_sequence: bucket.season_sequence,
      is_active: !bucket.is_active,
    })
      .then(() => onBucketsChanged())
      .catch((err) => { setToggleError(err.message); setToggleErrorSequence(bucket.season_sequence); })
      .finally(() => setTogglingSequence(null));
  };

  const handleDeleteClick = (bucket) => {
    const confirmed = window.confirm(`Delete bucket "${bucket.sequence_name}"? This cannot be undone.`);
    if (!confirmed) return;

    setDeletingSequence(bucket.season_sequence);
    setDeleteError(null);
    setDeleteErrorSequence(null);

    deleteBucket(comCode, trackingYear, bucket.season_sequence)
      .then(() => onBucketsChanged())
      .catch((err) => { setDeleteError(err.message); setDeleteErrorSequence(bucket.season_sequence); })
      .finally(() => setDeletingSequence(null));
  };

  const addDisabled = adding || newBucketName.trim().length === 0;

  const seasonalCount = displayBuckets.filter(isPositioned).length;
  let seasonalIdx = -1;
  const bucketRows = displayBuckets.map((bucket) => {
    const bucketIsOther = !isPositioned(bucket);
    if (!bucketIsOther) seasonalIdx += 1;
    const isEditing = bucket.season_sequence === editingSequence;

    return (
      <SeasonSequenceBucketRow
        key={bucket.season_sequence}
        bucket={bucket}
        trackingYear={trackingYear}
        isOther={bucketIsOther}
        canMoveUp={!bucketIsOther && seasonalIdx > 0}
        canMoveDown={!bucketIsOther && seasonalIdx < seasonalCount - 1}
        isReordering={reordering}
        onMoveUp={() => handleMove(bucket, "up")}
        onMoveDown={() => handleMove(bucket, "down")}
        isEditing={isEditing}
        editValue={isEditing ? editValue : ""}
        saving={isEditing && saving}
        error={isEditing ? renameError : null}
        onEditClick={() => handleEditClick(bucket)}
        onCancelClick={handleCancelClick}
        onSaveClick={() => handleSaveClick(bucket)}
        onEditValueChange={setEditValue}
        editMapsTo={isEditing ? editMapsTo : ""}
        onEditMapsToChange={setEditMapsTo}
        isToggling={bucket.season_sequence === togglingSequence}
        toggleError={bucket.season_sequence === toggleErrorSequence ? toggleError : null}
        onToggleActiveClick={() => handleToggleActiveClick(bucket)}
        isDeleting={bucket.season_sequence === deletingSequence}
        deleteError={bucket.season_sequence === deleteErrorSequence ? deleteError : null}
        onDeleteClick={() => handleDeleteClick(bucket)}
      />
    );
  });

  return (
    <>
      {reorderError && (
        <Typography variant="caption" color="error" display="block" sx={{ mb: 1 }}>
          {reorderError}
        </Typography>
      )}
      <TableContainer component={Paper}>
        <Table aria-label="franchise calendar buckets">
          <TableHead>
            <TableRow>
              <TableCell width="6%">Order</TableCell>
              <TableCell>Franchise Season</TableCell>
              <TableCell>Maps To</TableCell>
              <TableCell>Operational Seasons</TableCell>
              <TableCell align="center">Active</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayBuckets.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>No buckets found for this franchise/year.</TableCell>
              </TableRow>
            )}
            {bucketRows}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", mt: 2 }}>
        <TextField
          size="small"
          label="New bucket name"
          value={newBucketName}
          onChange={(e) => setNewBucketName(e.target.value)}
          error={!!addError}
          helperText={addError || ""}
          disabled={adding}
        />
        <TextField
          select
          size="small"
          label="Maps to"
          value={newBucketMapsTo}
          onChange={(e) => setNewBucketMapsTo(e.target.value)}
          disabled={adding}
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        >
          <option value="">— (unmapped)</option>
          <option value="1">Spring</option>
          <option value="2">Summer</option>
          <option value="3">Fall</option>
          <option value="4">Winter</option>
          <option value="0">Other</option>
        </TextField>
        <Button variant="contained" onClick={handleAddClick} disabled={addDisabled}>
          Add
        </Button>
      </Box>
    </>
  );
}
